// src/components/Sidebar/SidebarDailyCheckin.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
    ListItemButton,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Typography,
    Box,
    LinearProgress
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable'; // 출석 가능
import EventBusyIcon from '@mui/icons-material/EventBusy'; // 출석 완료
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { getCheckinStatus, postCheckin } from '../../api/points';
import { useUser } from '../../contexts/UserContext';

dayjs.extend(duration);

// HH:mm:ss 포맷
const formatCountdown = (ms) => {
    if (ms <= 0) return '00:00:00';
    const d = dayjs.duration(ms);
    const hh = String(d.hours()).padStart(2, '0');
    const mm = String(d.minutes()).padStart(2, '0');
    const ss = String(d.seconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
};

// 진행률 %
const calculateProgress = (value, total) => {
    if (!total || total <= 0) return 0;
    const pct = (value / total) * 100;
    return Math.min(100, Math.max(0, pct));
};

export default function SidebarDailyCheckin({ onItemClick }) {
    const { refreshUserInfo } = useUser();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [countdown, setCountdown] = useState('');

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await getCheckinStatus();
            setStatus(data);
        } catch (err) {
            console.error('출석 상태 로딩 실패:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // 카운트다운
    useEffect(() => {
        if (!status?.checked_today || !status?.next_reset_at_kst) return;

        const interval = setInterval(() => {
            const now = dayjs();
            const nextReset = dayjs(status.next_reset_at_kst);
            const diff = nextReset.diff(now);

            if (diff <= 0) {
                setCountdown('초기화!');
                // 초기화 즉시 상태 재조회
                fetchStatus();
            } else {
                setCountdown(formatCountdown(diff));
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [status?.checked_today, status?.next_reset_at_kst, fetchStatus]);

    const handleCheckin = async (e) => {
        e.stopPropagation();
        setSubmitting(true);
        try {
            const { data: checkinResult } = await postCheckin();

            // (선택) 보너스 안내
            if (checkinResult?.bonus_earned > 0 && checkinResult?.bonus_message) {
                alert(`축하합니다! ${checkinResult.bonus_message}\n+${checkinResult.bonus_earned} Leaf`);
            }

            await refreshUserInfo();
            await fetchStatus();
            onItemClick?.(); // 사이드바 닫기 (옵셔널)
        } catch (err) {
            console.error('출석체크 실패:', err);
            alert(err?.response?.data?.detail || '출석체크에 실패했습니다.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <ListItemButton disabled>
                <ListItemIcon><CircularProgress size={20} /></ListItemIcon>
                <ListItemText primary="출석 정보 로딩 중..." />
            </ListItemButton>
        );
    }

    // 주간 진행률 계산 (보너스 직후 0%로 보이지 않도록 보정)
    const consecutiveDays = status?.consecutive_days ?? 0;
    let weekRemainder = consecutiveDays % 7;
    if (consecutiveDays > 0 && weekRemainder === 0) {
        weekRemainder = 7; // 7/7로 꽉 찬 상태로 표시
    }
    const weeklyProgressPct = calculateProgress(weekRemainder, 7);

    const monthChecked = status?.current_month_checked_days ?? 0;
    const monthTotal = status?.current_month_total_days ?? 30;
    const monthlyProgressPct = calculateProgress(monthChecked, monthTotal);

    // ── 주간 표시용(라벨) 숫자: 0~7 사이로 보이게
    const weeklyDisplay =
        consecutiveDays === 0
            ? 0
            : (consecutiveDays % 7 === 0 ? 7 : consecutiveDays % 7);

    // ── 월간 표시용(라벨) 숫자: 월 총일수 초과해도 capped
    const monthDisplayChecked = Math.min(monthChecked, monthTotal);



    if (status?.checked_today) {
        return (
            <Box sx={{ p: 1, pl: 2 }}>
                <ListItemButton disabled sx={{ pl: 0 }}>
                    <ListItemIcon><EventBusyIcon /></ListItemIcon>
                    <ListItemText
                        primary="출석 완료"
                        secondary={countdown}
                        secondaryTypographyProps={{ fontFamily: 'monospace', color: 'primary.main' }}
                    />
                </ListItemButton>

                {/* 연속 출석 현황 */}
                <Box sx={{ mt: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption">주간 출석</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {weeklyDisplay}일 / 7일
                        </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={weeklyProgressPct} />

                    {/* ✅ 힌트 메시지 추가 */}
                    {weekRemainder === 6 && (
                        <Typography variant="caption" color="secondary" sx={{ fontWeight: 600 }}>
                            내일 출석 시 +50 Leaf 보너스!
                        </Typography>
                    )}
                </Box>

                {/* 월간 출석 현황 */}
                <Box sx={{ mt: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption">월간 출석</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {monthDisplayChecked}일 / {monthTotal}일
                        </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={monthlyProgressPct} />

                    {/* ✅ 월간 보너스 힌트 추가 */}
                    {monthChecked === monthTotal - 1 && (
                        <Typography variant="caption" color="secondary" sx={{ fontWeight: 600 }}>
                            내일 출석 시 월간 보너스 +500 Leaf!
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <ListItemButton onClick={handleCheckin} disabled={submitting}>
            <ListItemIcon>
                {submitting ? <CircularProgress size={20} /> : <EventAvailableIcon color="primary" />}
            </ListItemIcon>
            <ListItemText
                primary="출석체크 하기"
                primaryTypographyProps={{ fontWeight: 600, color: 'primary.main' }}
                secondary="+10 Leaf"
            />
        </ListItemButton>
    );
}
